"use client";

import { useEffect, useState, type ReactNode } from "react";
import { ClerkProvider } from "@/lib/auth-shim/client";
import { dark } from "@clerk/themes";

type Theme = "light" | "dark";

function readTheme(): Theme {
    if (typeof document === "undefined") return "dark";
    const attr = document.documentElement.getAttribute("data-theme");
    return attr === "light" ? "light" : "dark";
}

function buildAppearance(theme: Theme) {
    const isDark = theme === "dark";

    const colorBackground = isDark ? "#0a0a0f" : "#ffffff";
    const colorInputBackground = isDark ? "#111118" : "#f8fafc";
    const colorText = isDark ? "#e8e8ed" : "#0f172a";
    const colorTextSecondary = isDark ? "#7a7a8a" : "#64748b";
    const cardBorder = isDark
        ? "1px solid rgba(255, 255, 255, 0.08)"
        : "1px solid rgba(15, 23, 42, 0.12)";
    const cardShadow = isDark
        ? "0 8px 32px rgba(0, 0, 0, 0.5)"
        : "0 8px 32px rgba(15, 23, 42, 0.08)";
    const inputBorder = isDark
        ? "1px solid rgba(255, 255, 255, 0.08)"
        : "1px solid rgba(15, 23, 42, 0.12)";
    const socialButtonHover = isDark ? "#1a1a24" : "#f1f5f9";
    const modalBackdrop = isDark
        ? "rgba(5, 5, 8, 0.8)"
        : "rgba(15, 23, 42, 0.4)";

    return {
        baseTheme: isDark ? dark : undefined,
        variables: {
            colorBackground,
            colorInputBackground,
            colorPrimary: "#2563eb",
            colorText,
            colorTextSecondary,
            colorDanger: "#ef4444",
            colorNeutral: colorText,
            borderRadius: "0.75rem",
            fontFamily: 'var(--font, "Inter"), system-ui, -apple-system, sans-serif',
            fontSize: "0.9rem",
        },
        elements: {
            card: {
                backgroundColor: colorBackground,
                border: cardBorder,
                boxShadow: cardShadow,
            },
            headerTitle: {
                fontFamily: 'var(--font-display, "Inter"), system-ui, sans-serif',
                fontWeight: 500,
                letterSpacing: "-0.02em",
            },
            headerSubtitle: {
                color: colorTextSecondary,
            },
            socialButtonsBlockButton: {
                backgroundColor: colorInputBackground,
                border: inputBorder,
                "&:hover": {
                    backgroundColor: socialButtonHover,
                },
            },
            formFieldInput: {
                backgroundColor: colorInputBackground,
                border: inputBorder,
                "&:focus": {
                    borderColor: "#2563eb",
                    boxShadow: "0 0 0 2px rgba(37, 99, 235, 0.2)",
                },
            },
            formButtonPrimary: {
                background: "linear-gradient(135deg, #2563eb, #3b82f6)",
                "&:hover": {
                    background: "linear-gradient(135deg, #60a5fa, #60a5fa)",
                },
            },
            footerActionLink: {
                color: "#2563eb",
                "&:hover": {
                    color: "#60a5fa",
                },
            },
            modalBackdrop: {
                backgroundColor: modalBackdrop,
                backdropFilter: "blur(8px)",
            },
            userButtonAvatarBox: {
                width: "28px",
                height: "28px",
            },
        },
    };
}

export function ClerkThemeProvider({ children }: { children: ReactNode }) {
    const [theme, setTheme] = useState<Theme>("dark");

    useEffect(() => {
        setTheme(readTheme());

        // Watch <html data-theme="..."> so Clerk's appearance re-syncs the
        // instant the user clicks the floating ThemeToggle button. Without
        // this, sign-in / user-button modals stay locked to whatever theme
        // was active at first paint.
        const observer = new MutationObserver(() => setTheme(readTheme()));
        observer.observe(document.documentElement, {
            attributes: true,
            attributeFilter: ["data-theme"],
        });
        return () => observer.disconnect();
    }, []);

    return (
        <ClerkProvider
            appearance={buildAppearance(theme)}
            afterSignInUrl="/"
            afterSignUpUrl="/"
        >
            {children}
        </ClerkProvider>
    );
}
